/**
 * Error state of the B2B plan selection screen.
 *
 * A school admin lands on that screen whenever their school has no paid
 * access. When the plan preview cannot load (most often because the school
 * has no billing plan configured: `GET /billing/schools/{id}/plan-preview`
 * answers 400 "School billing plan not configured"), retrying alone can never
 * succeed, so the screen must also offer a way to reach support and to sign
 * out instead of a dead end.
 */

/** `detail` of the 400 from plan-preview (backend `ERROR_SCHOOL_BILLING_NOT_CONFIGURED`). */
export const SCHOOL_BILLING_NOT_CONFIGURED_DETAIL = 'School billing plan not configured';

export const SCHOOL_SUPPORT_EMAIL = 'support@mentorcoaching.ai';

export type PlanPreviewErrorKind = 'not_configured' | 'generic';

export function planPreviewErrorKind(error: unknown): PlanPreviewErrorKind {
  const err = error as { status?: unknown; detail?: unknown } | null | undefined;
  if (err && err.status === 400 && err.detail === SCHOOL_BILLING_NOT_CONFIGURED_DETAIL) {
    return 'not_configured';
  }
  return 'generic';
}

/** Stable analytics code for the error state (`app_b2b_plan_selection_error_shown`). */
export type B2BPlanErrorCode =
  | 'school_billing_not_configured'
  | 'plan_preview_failed'
  | 'activation_pending'
  | 'no_school';

export function planPreviewErrorCode(kind: PlanPreviewErrorKind): B2BPlanErrorCode {
  return kind === 'not_configured' ? 'school_billing_not_configured' : 'plan_preview_failed';
}

/** i18n key of the message shown for a failed plan preview. */
export function planPreviewErrorMessageKey(kind: PlanPreviewErrorKind): string {
  return kind === 'not_configured'
    ? 'b2bPlanSelection.errors.notConfigured'
    : 'b2bPlanSelection.errors.fetchPlan';
}

export function schoolSupportMailto(): string {
  return `mailto:${SCHOOL_SUPPORT_EMAIL}?subject=${encodeURIComponent('School activation')}`;
}
